import { useState, useRef, useCallback } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  useCreateVault,
  type CreateVaultInput,
  type EncryptedVaultPayload,
} from '@/hooks/useCreateVault.ts'
import { env } from '@/config/env.ts'
import { cn } from '@/utils/cn.ts'
import { triggerBrowserDownload } from '@/utils/downloadBlob.ts'
import { CreateVaultBetaModal } from './CreateVaultBetaModal.tsx'

const VAULT_BETA_ACK_KEY = 'bitslegacy_vault_beta_ack'

function readVaultBetaAcknowledged(): boolean {
  try {
    return sessionStorage.getItem(VAULT_BETA_ACK_KEY) === '1'
  } catch {
    return false
  }
}

const STEPS = [
  'encrypting',
  'uploading',
  'contract',
  'metadata',
  'notifying',
  'done',
] as const

const STEP_KEYS: Record<string, string> = {
  encrypting: 'createVault.stepEncrypting',
  uploading: 'createVault.stepUploading',
  contract: 'createVault.stepContract',
  metadata: 'createVault.stepMetadata',
  notifying: 'createVault.stepNotifying',
  done: 'createVault.stepDone',
}

const inputClass =
  'w-full rounded-md border border-line bg-surface-muted px-4 py-3 font-inherit text-[0.9375rem] text-fg outline-none transition-[border-color] duration-fast placeholder:text-fg-muted focus:border-accent'

const labelClass = 'text-sm font-medium text-fg-secondary'

const labelRequiredClass = "after:ml-0.5 after:text-error after:content-['*']"

// Lit Action responses are capped at 102400 bytes. Encrypting full files inside
// the action can exceed this quickly, so we enforce a 70KB limit for all file uploads.
const MAX_FILE_SIZE_KB = 70
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_KB * 1024

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export function CreateVaultPage() {
  const { t } = useTranslation()
  const { step, loading, error, encryptedPayload, create } = useCreateVault()
  const fileRef = useRef<HTMLInputElement>(null)
  const [submitPending, setSubmitPending] = useState(false)
  const [betaAcknowledged, setBetaAcknowledged] = useState(
    readVaultBetaAcknowledged,
  )

  const handleBetaContinue = useCallback(() => {
    try {
      sessionStorage.setItem(VAULT_BETA_ACK_KEY, '1')
    } catch {
      /* ignore quota / private mode */
    }
    setBetaAcknowledged(true)
  }, [])

  const [vaultType, setVaultType] = useState<'text' | 'file'>('text')
  const [textContent, setTextContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [beneficiaryAddress, setBeneficiaryAddress] = useState('')
  const [beneficiaryEmail, setBeneficiaryEmail] = useState('')
  const [beneficiaryPhone, setBeneficiaryPhone] = useState('')
  const [countdownValue, setCountdownValue] = useState('')
  const [countdownUnit, setCountdownUnit] = useState<'minutes' | 'days'>(
    env.isDevelopment ? 'minutes' : 'days',
  )
  const [vaultName, setVaultName] = useState('')

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size === 0) {
      setFileError(t('createVault.fileEmpty'))
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    if (f.size > MAX_FILE_SIZE_BYTES) {
      setFileError(
        t('createVault.fileTooLarge', {
          size: MAX_FILE_SIZE_KB,
          actual: formatFileSize(f.size),
        }),
      )
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    setFileError(null)
    setFile(f)
  }

  const handleSwitchType = (next: 'text' | 'file') => {
    setVaultType(next)
    setFileError(null)
    if (next === 'text') {
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (vaultType === 'file') {
      if (!file) {
        setFileError(t('createVault.fileRequired'))
        return
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError(
          t('createVault.fileTooLarge', {
            size: MAX_FILE_SIZE_KB,
            actual: formatFileSize(file.size),
          }),
        )
        return
      }
    }

    setSubmitPending(true)

    const countdownMinutes =
      countdownUnit === 'days'
        ? Number(countdownValue) * 24 * 60
        : Number(countdownValue)

    const input: CreateVaultInput = {
      type: vaultType,
      content: vaultType === 'text' ? textContent : '',
      file: vaultType === 'file' ? (file ?? undefined) : undefined,
      beneficiaryAddress: beneficiaryAddress as `0x${string}`,
      beneficiaryEmail,
      beneficiaryPhone: beneficiaryPhone || undefined,
      countdownMinutes,
      vaultName: vaultName || undefined,
    }

    const ok = await create(input)
    if (!ok) setSubmitPending(false)
  }

  const isFormValid =
    (vaultType === 'text'
      ? textContent.trim().length > 0
      : !!file && file.size > 0 && file.size <= MAX_FILE_SIZE_BYTES) &&
    beneficiaryAddress.startsWith('0x') &&
    beneficiaryAddress.length === 42 &&
    beneficiaryEmail.includes('@') &&
    Number(countdownValue) > 0

  const submitLabel = submitPending
    ? loading && STEP_KEYS[step]
      ? t(STEP_KEYS[step])
      : 'Starting...'
    : t('createVault.submit')

  const handleDownloadBackup = (payload: EncryptedVaultPayload) => {
    const json = JSON.stringify(payload, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const filename = `vault-backup-${payload.exportedAt.replace(/[:.]/g, '-')}.json`
    triggerBrowserDownload(blob, filename)
  }

  if (step === 'done') {
    return (
      <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
        <div className="border-success-border bg-success-muted text-success rounded-md border p-6 text-center">
          <h2 className="mb-2 text-lg font-semibold">
            {t('createVault.successTitle')}
          </h2>
          <p>{t('createVault.successMessage')}</p>
          <p>{t('createVault.successNotified')}</p>
          <p className="mt-4">
            <Link to="/dashboard" className="text-accent underline">
              {t('createVault.backToDashboard')}
            </Link>
          </p>
        </div>

        {encryptedPayload && (
          <div className="border-line bg-surface-card mt-6 rounded-lg border p-6">
            <h3 className="text-fg mb-1 text-[1rem] font-semibold">
              {t('createVault.downloadBackup')}
            </h3>
            <p className="text-fg-secondary mb-4 text-sm">
              {t('createVault.downloadBackupHint')}
            </p>
            <button
              type="button"
              id="download-encrypted-backup"
              className="border-line bg-surface-muted text-fg duration-fast hover:border-accent cursor-pointer rounded-md border px-6 py-3 text-[0.9375rem] font-semibold transition-all"
              onClick={() => handleDownloadBackup(encryptedPayload)}
            >
              ⬇ {t('createVault.downloadBackup')}
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      <CreateVaultBetaModal
        open={!betaAcknowledged && !loading}
        onContinue={handleBetaContinue}
      />
      <h1 className="text-fg mb-1 text-[1.75rem] font-bold max-md:text-[1.375rem]">
        {t('createVault.title')}
      </h1>
      <p className="text-fg-secondary mb-12">{t('createVault.subtitle')}</p>

      {loading && (
        <div className="border-line bg-surface-card mb-8 flex flex-col gap-4 rounded-lg border p-6">
          {STEPS.map((s) => {
            const currentIdx = STEPS.indexOf(step as typeof s)
            const idx = STEPS.indexOf(s)
            return (
              <div
                key={s}
                className={cn(
                  'text-fg-muted flex items-center gap-4 text-sm',
                  idx < currentIdx && 'text-success',
                  idx === currentIdx && 'text-accent',
                )}
              >
                <span>
                  {idx < currentIdx ? '✓' : idx === currentIdx ? '●' : '○'}
                </span>
                <span>{t(STEP_KEYS[s])}</span>
              </div>
            )
          })}
        </div>
      )}

      {!loading && betaAcknowledged && (
        <form
          className="flex max-w-[600px] flex-col gap-12"
          onSubmit={handleSubmit}
        >
          <div>
            <h3 className="text-fg mb-4 text-lg font-semibold">
              {t('createVault.vaultContent')}
            </h3>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <span className={labelClass}>{t('createVault.vaultType')}</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={cn(
                      'duration-fast cursor-pointer rounded-md border px-4 py-2 text-sm font-medium transition-all',
                      vaultType === 'text'
                        ? 'border-accent bg-accent-muted text-accent'
                        : 'border-line bg-surface-muted text-fg-secondary',
                    )}
                    onClick={() => handleSwitchType('text')}
                  >
                    {t('createVault.text')}
                  </button>
                  <button
                    type="button"
                    className={cn(
                      'duration-fast cursor-pointer rounded-md border px-4 py-2 text-sm font-medium transition-all',
                      vaultType === 'file'
                        ? 'border-accent bg-accent-muted text-accent'
                        : 'border-line bg-surface-muted text-fg-secondary',
                    )}
                    onClick={() => handleSwitchType('file')}
                  >
                    {t('createVault.file')}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className={labelClass} htmlFor="vault-name">
                  {t('createVault.vaultName')}
                </label>
                <input
                  id="vault-name"
                  type="text"
                  className={inputClass}
                  value={vaultName}
                  onChange={(e) => setVaultName(e.target.value)}
                  placeholder={t('createVault.vaultNamePlaceholder')}
                />
              </div>

              {vaultType === 'text' ? (
                <div className="flex flex-col gap-1">
                  <label
                    className={cn(labelClass, labelRequiredClass)}
                    htmlFor="vault-text"
                  >
                    {t('createVault.content')}
                  </label>
                  <textarea
                    id="vault-text"
                    className={cn(inputClass, 'min-h-[160px] resize-y')}
                    value={textContent}
                    onChange={(e) => setTextContent(e.target.value)}
                    placeholder={t('createVault.contentPlaceholder')}
                    required
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <span className={cn(labelClass, labelRequiredClass)}>
                    {t('createVault.file')}
                  </span>
                  <div
                    className={cn(
                      'duration-fast flex min-h-[160px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-[border-color]',
                      fileError
                        ? 'border-error hover:border-error'
                        : 'border-line hover:border-accent',
                    )}
                    onClick={() => fileRef.current?.click()}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && fileRef.current?.click()
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <p className="text-fg-muted text-center text-[0.9375rem]">
                      {t('createVault.clickToSelect')}
                    </p>
                    <p className="text-fg-muted mt-1 text-center text-xs">
                      {t('createVault.maxFileSize', { size: MAX_FILE_SIZE_KB })}
                    </p>
                    {file && (
                      <p className="text-accent mt-3 text-center text-sm break-all">
                        {file.name}{' '}
                        <span className="text-fg-muted">
                          ({formatFileSize(file.size)})
                        </span>
                      </p>
                    )}
                  </div>
                  {fileError && (
                    <p className="text-error mt-1 text-xs" role="alert">
                      {fileError}
                    </p>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-fg mb-4 text-lg font-semibold">
              {t('createVault.beneficiaryDetails')}
            </h3>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <label
                  className={cn(labelClass, labelRequiredClass)}
                  htmlFor="ben-address"
                >
                  {t('createVault.walletAddress')}
                </label>
                <input
                  id="ben-address"
                  type="text"
                  className={inputClass}
                  value={beneficiaryAddress}
                  onChange={(e) => setBeneficiaryAddress(e.target.value)}
                  placeholder={t('createVault.walletPlaceholder')}
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <label
                  className={cn(labelClass, labelRequiredClass)}
                  htmlFor="ben-email"
                >
                  {t('createVault.email')}
                </label>
                <input
                  id="ben-email"
                  type="email"
                  className={inputClass}
                  value={beneficiaryEmail}
                  onChange={(e) => setBeneficiaryEmail(e.target.value)}
                  placeholder={t('createVault.emailPlaceholder')}
                  required
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className={labelClass} htmlFor="ben-phone">
                  {t('createVault.phone')}
                </label>
                <input
                  id="ben-phone"
                  type="tel"
                  className={inputClass}
                  value={beneficiaryPhone}
                  onChange={(e) => setBeneficiaryPhone(e.target.value)}
                  placeholder={t('createVault.phonePlaceholder')}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-fg mb-4 text-lg font-semibold">
              {t('createVault.countdownTimer')}
            </h3>
            <div className="flex flex-col gap-4 max-md:gap-2 md:flex-row md:items-end">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <label
                  className={cn(labelClass, labelRequiredClass)}
                  htmlFor="countdown-value"
                >
                  {t('createVault.duration')}
                </label>
                <input
                  id="countdown-value"
                  type="number"
                  className={inputClass}
                  value={countdownValue}
                  onChange={(e) => setCountdownValue(e.target.value)}
                  min="1"
                  placeholder={t('createVault.durationPlaceholder')}
                  required
                />
              </div>
              <div className="flex w-full flex-col gap-1 md:w-[120px]">
                <label className={labelClass} htmlFor="countdown-unit">
                  {t('createVault.unit')}
                </label>
                <select
                  id="countdown-unit"
                  className={inputClass}
                  value={countdownUnit}
                  onChange={(e) =>
                    setCountdownUnit(e.target.value as 'minutes' | 'days')
                  }
                >
                  <option value="minutes">{t('createVault.minutes')}</option>
                  <option value="days">{t('createVault.days')}</option>
                </select>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="bg-gradient-brand text-fg-inverse duration-fast self-start rounded-md border-none px-8 py-3.5 text-base font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!isFormValid || loading || submitPending}
          >
            {submitLabel}
          </button>
          {error && (
            <div className="border-error-border bg-error-muted text-error rounded-md border p-4 text-sm">
              {error}
            </div>
          )}
        </form>
      )}
    </div>
  )
}
